import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import money from "astrid-helpers/src/money";

import Flex from "../../../../../components/Flex/Flex";
import usePriceTypes from "../../../../prices/hooks/usePriceTypes";
import { priceAccessor } from "../../../../prices/utils/priceAccessors";
import Label from "../../../../ui/components/Label/Label";

import OpenCostDropdownMenu from "../../OpenCostDropdownMenu/OpenCostDropdownMenu";

import ApprovePriceAction from "../components/ApprovePriceAction";
import CostSessionPopup from "../components/CostSessionPopup";

export default function useOpenCostsTableColumns({ partyId, production }) {
	const { t } = useTranslation();

	const priceTypeNames = usePriceTypes();

	return useMemo(
		() => [
			{
				id: "secondParty.name",
				filter: "text",
				Header: t("secondParty", "Second party"),
			},
			{
				id: "price.type",
				filter: "select",
				Header: t("type", "Type"),
				accessor: ({ price }) => priceTypeNames[price?.type],
				Cell: ({ value, row }) => (
					<Flex justifyContent="flex-start" gap={4}>
						{value} {row.original.session && <CostSessionPopup cost={row.original} />}
					</Flex>
				),
			},
			{
				id: "price.price",
				filter: "text",
				Header: t("price", "Price"),
				accessor: (cost) =>
					cost?.price?.price === null ? t("hiddenPrice", "Hidden price") : priceAccessor(cost.price),
			},
			{
				id: "estimation",
				Header: t("estimated", "Estimated"),
				textAlign: "right",
				accessor: ({ expense, estimation, secondParty }) => {
					if (!estimation) {
						return null;
					}

					return estimation.value * (expense ? -1 : 1) * (partyId === secondParty?.id ? -1 : 1);
				},
				Cell: ({ value, row }) => (value ? money(value, row.original.price?.currency) : null),
			},
			{
				id: "amount",
				Header: t("open", "Open"),
				textAlign: "right",
				accessor: ({ total, expense, secondParty }) => {
					return total * (expense ? -1 : 1) * (partyId === secondParty?.id ? -1 : 1);
				},
				Cell: ({ value, row }) =>
					value ? <Label color="blue">{money(value, row.original.price?.currency)}</Label> : null,
			},
			{
				id: "actions",
				textAlign: "right",
				collapsing: true,
				Cell: ({ row }) => (
					<div style={{ whiteSpace: "nowrap" }}>
						<ApprovePriceAction price={row.original.price} />

						<OpenCostDropdownMenu
							partyId={partyId}
							openCost={row.original}
							production={production}
							isExpanded={row.isExpanded}
							expandedProps={row.getToggleRowExpandedProps({
								disabled: !row.canExpand,
							})}
						/>
					</div>
				),
			},
		],
		[priceTypeNames, partyId, production, t],
	);
}
